import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { TranslocoLocaleWrapperService } from 'app/fuse-template/core/transloco/translocoLocale.service';
import { base64ToArrayBuffer, saveByteArray } from 'app/helpers/blobDownload';
import { ExposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes';
import { ExposedStateResponsibilityGroupChargedEntriesExcelRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupChargedEntriesExcelRes';
import { ExposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes';
import { ExposedStateResponsibilityGroupPrepareEntriesExcelRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupPrepareEntriesExcelRes';
import { PensionService } from 'app/services/api/pension/pension.service';
import { JobsService } from 'app/services/api/queue/jobs.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { JOB_STATUS } from 'common/constants/queue.constants';
import { STATE_RESPONSIBILITY_GROUP_EXCEL_TYPES } from 'common/constants/stateResponsibilityGroupExcelTypes.constants';

@Component({
    selector: 'app-pension-state-responsibility-group',
    templateUrl: './pension-state-responsibility-group.component.html',
    styleUrls: ['./pension-state-responsibility-group.component.scss'],
})
export class PensionStateResponsibilityGroupComponent implements OnInit {
    //#region Globals, Init and Constructor

    form: UntypedFormGroup;
    formEnabledPrepare: boolean = false;
    formEnabledCharge: boolean = false;

    checkIfLoadingPreparationTimeout: ReturnType<typeof setTimeout>;
    checkIfLoadingChargeTimeout: ReturnType<typeof setTimeout>;

    prepareCheckIfLoading: ExposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes;
    chargeCheckIfLoading: ExposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes;

    excelTypes = STATE_RESPONSIBILITY_GROUP_EXCEL_TYPES;

    constructor(
        private jobsService: JobsService,
        private formBuilder: UntypedFormBuilder,
        private pensionService: PensionService,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        private translationService: TranslationService,
        private translocoLocaleService: TranslocoLocaleWrapperService,
        private _fuseConfirmationService: FuseConfirmationService
    ) {
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_load_site'));
    }

    ngOnInit(): void {
        this.initForm();
        this.checkIfLoadingPreparation(true);
        this.checkIfLoadingCharge(true);
    }

    ngOnDestroy(): void {
        clearTimeout(this.checkIfLoadingPreparationTimeout);
        clearTimeout(this.checkIfLoadingChargeTimeout);
    }

    initForm() {
        this.form = this.formBuilder.group({
            chargePercentage: new UntypedFormControl('4,1', [Validators.required]),
        });
        this.form.markAsDirty();
    }

    //#endregion

    //#region Preparation

    prepareEntries() {
        if (!this.formEnabledCharge) {
            this.alertsService.changealertHeader(this.translationService.translate('label_cannot_proceed'));
            this.alertsService.changeAlertErrorMessage(this.translationService.translate('info_cannot_proceed_wait'));
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.formEnabledPrepare = false;
        this.pensionService
            .stateResponsibilityGroupPrepareEntries(this.formatChargePercentageToRatio(this.form.get('chargePercentage').value))
            .finally(() => {
                this.checkIfLoadingPreparation();
            });
    }

    checkIfLoadingPreparation(displayLoader = false) {
        if (displayLoader) {
            this.loaderService.changeActiveLoaders(1);
        }

        this.pensionService.stateResponsibilityGroupPrepareEntriesCheckIfLoading().then((res) => {
            this.prepareCheckIfLoading = res;
            if (res.isLoading) {
                this.checkIfLoadingPreparationTimeout = setTimeout(() => {
                    this.checkIfLoadingPreparation();
                }, 5000);
            } else {
                this.formEnabledPrepare = true;
            }
            if (displayLoader) {
                this.loaderService.changeActiveLoaders(-1);
            }
        });
    }

    prepareForceAnotherAction() {
        let confirmationForm = this.formBuilder.group({
            title: 'Framkvæma viðbótarkeyrslu',
            message: 'Ertu viss um að þú viljir framkvæma viðbótarkeyrslu?',
            icon: {
                show: false,
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: this.translationService.translate('label_cancel'),
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(confirmationForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.prepareEntries();
            }
        });
    }

    prepareEntriesCheckDisable() {
        return (
            !this.formEnabledPrepare ||
            !this.formEnabledCharge ||
            new Date(this.prepareCheckIfLoading?.lastMessageCreatedAt) > new Date(this.chargeCheckIfLoading?.lastMessageCreatedAt)
        );
    }

    //#endregion

    //#region Charge

    chargeEntries() {
        if (!this.formEnabledPrepare) {
            this.alertsService.changealertHeader(this.translationService.translate('label_cannot_proceed'));
            this.alertsService.changeAlertErrorMessage(this.translationService.translate('info_cannot_proceed_wait'));
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.formEnabledCharge = false;
        this.pensionService.stateResponsibilityGroupChargeEntries().finally(() => {
            this.checkIfLoadingCharge();
        });
    }

    checkIfLoadingCharge(displayLoader = false) {
        if (displayLoader) {
            this.loaderService.changeActiveLoaders(1);
        }

        this.pensionService.stateResponsibilityGroupChargeEntriesCheckIfLoading(false).then((res) => {
            this.chargeCheckIfLoading = res;
            if (res.isLoading) {
                this.checkIfLoadingPreparationTimeout = setTimeout(() => {
                    this.checkIfLoadingCharge();
                }, 5000);
            } else {
                this.formEnabledCharge = true;
            }
            if (displayLoader) {
                this.loaderService.changeActiveLoaders(-1);
            }
        });
    }

    chargeEntriesCheckDisableButton() {
        return new Date(this.chargeCheckIfLoading?.lastMessageCreatedAt) > new Date(this.prepareCheckIfLoading?.lastMessageCreatedAt);
    }

    //#endregion

    //#region Excel sheets

    onGenerateExcel(type: string) {
        if (!this.formEnabledCharge || !this.formEnabledPrepare) {
            this.alertsService.changealertHeader(this.translationService.translate('label_cannot_proceed'));
            this.alertsService.changeAlertErrorMessage(this.translationService.translate('info_cannot_proceed_wait'));
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.loaderService.changeActiveLoaders(1);

        if (type == this.excelTypes.PREPARATION) {
            this.pensionService.stateResponsibilityGroupPrepareCreateExcel().then((jobId: number) => {
                this.fetchExcelPreparation(jobId);
            });
        } else if (type == this.excelTypes.CHARGED) {
            this.pensionService.stateResponsibilityGroupChargedCreateExcel().then((jobId: number) => {
                this.fetchExcelCharged(jobId);
            });
        } else if (type == this.excelTypes.ALL) {
            this.pensionService.stateResponsibilityGroupAllCreateExcel().then((jobId: number) => {
                this.fetchExcelAll(jobId);
            });
        }
    }

    fetchExcelPreparation(jobId) {
        this.jobsService.getJobDetails(jobId, true, false).then((job) => {
            if (job.status == JOB_STATUS.FINISHED) {
                this.pensionService
                    .stateResponsibilityGroupPrepareFetchExcel(jobId)
                    .then((res: ExposedStateResponsibilityGroupPrepareEntriesExcelRes) => {
                        var sampleArr = base64ToArrayBuffer(res.base64File);
                        saveByteArray(res.fileName, sampleArr);
                    });
                this.loaderService.changeActiveLoaders(-1);
            } else if (job.status == JOB_STATUS.FINISHED_WITH_FAILURES) {
                this.alertsService.changealertHeader(this.translationService.translate('label_failed'));
                this.alertsService.changeAlertErrorMessage(this.translationService.translate('label_error_came_up'));
                this.alertsService.changeAlertErrorShow(true);
                this.loaderService.changeActiveLoaders(-1);
            } else {
                setTimeout(() => {
                    this.fetchExcelPreparation(jobId);
                }, 5000);
            }
        });
    }

    fetchExcelCharged(jobId) {
        this.jobsService.getJobDetails(jobId, true, false).then((job) => {
            if (job.status == JOB_STATUS.FINISHED) {
                this.pensionService
                    .stateResponsibilityGroupChargedFetchExcel(jobId)
                    .then((res: ExposedStateResponsibilityGroupChargedEntriesExcelRes) => {
                        var sampleArr = base64ToArrayBuffer(res.base64File);
                        saveByteArray(res.fileName, sampleArr);
                    });
                this.loaderService.changeActiveLoaders(-1);
            } else if (job.status == JOB_STATUS.FINISHED_WITH_FAILURES) {
                this.alertsService.changealertHeader(this.translationService.translate('label_failed'));
                this.alertsService.changeAlertErrorMessage(this.translationService.translate('label_error_came_up'));
                this.alertsService.changeAlertErrorShow(true);
                this.loaderService.changeActiveLoaders(-1);
            } else {
                setTimeout(() => {
                    this.fetchExcelCharged(jobId);
                }, 5000);
            }
        });
    }

    fetchExcelAll(jobId) {
        this.jobsService.getJobDetails(jobId, true, false).then((job) => {
            if (job.status == JOB_STATUS.FINISHED) {
                this.pensionService
                    .stateResponsibilityGroupAllFetchExcel(jobId)
                    .then((res: ExposedStateResponsibilityGroupChargedEntriesExcelRes) => {
                        var sampleArr = base64ToArrayBuffer(res.base64File);
                        saveByteArray(res.fileName, sampleArr);
                    });
                this.loaderService.changeActiveLoaders(-1);
            } else if (job.status == JOB_STATUS.FINISHED_WITH_FAILURES) {
                this.alertsService.changealertHeader(this.translationService.translate('label_failed'));
                this.alertsService.changeAlertErrorMessage(this.translationService.translate('label_error_came_up'));
                this.alertsService.changeAlertErrorShow(true);
                this.loaderService.changeActiveLoaders(-1);
            } else {
                setTimeout(() => {
                    this.fetchExcelAll(jobId);
                }, 5000);
            }
        });
    }

    //#endregion

    //#region Helper methods

    formatChargePercentageToRatio(chargeRatio: string) {
        return +(this.translocoLocaleService.parseLocalizedNumber(chargeRatio) / 100).toFixed(4);
    }

    //#endregion
}
